import {ColumnCellClassRuleFn, ColumnSpec} from '@xh/hoist/cmp/grid';
import {CubeField, CubeFieldSpec, FieldType, Store, StoreRecord} from '@xh/hoist/data';
import {fmtNumber, fmtPercent, numberRenderer, percentRenderer, priceRenderer} from '@xh/hoist/format';
import './PositionColumns.scss';
import {span, strong} from '@xh/hoist/cmp/layout';
import {WeightedAvgPriceAggregator} from './WeightedAvgPriceAggregator';
import {MINI_CHART_CELL, rangeBar, shareBar} from './miniCharts/MiniCharts';
import {intradaySparkline} from './miniCharts/IntradaySparkline';
import {startsWith, sumBy} from 'lodash';

export const tickerField: CubeFieldSpec = {
    name: 'ticker',
    type: FieldType.STRING,
    isDimension: true
};

const portfolioIdField: CubeFieldSpec = {
    name: 'portfolioId',
    type: FieldType.INT,
    isDimension: true,
    aggregator: CubeField.uniqueAggregator
};

const securityIdField: CubeFieldSpec = {
    name: 'securityId',
    type: FieldType.INT,
    isDimension: true,
    aggregator: CubeField.uniqueAggregator
};

const portfolioNameField: CubeFieldSpec = {
    name: 'portfolioName',
    type: FieldType.STRING,
    isDimension: true,
    aggregator: CubeField.uniqueAggregator
};

const portfolioManagementTypeField: CubeFieldSpec = {
    name: 'portfolioManagementType',
    type: FieldType.STRING,
    isDimension: true,
    aggregator: CubeField.uniqueAggregator
};

const portfolioTaxTypeField: CubeFieldSpec = {
    name: 'portfolioTaxType',
    type: FieldType.STRING,
    isDimension: true,
    aggregator: CubeField.uniqueAggregator
};

const portfolioHoldingTypeField: CubeFieldSpec = {
    name: 'portfolioHoldingType',
    type: FieldType.STRING,
    isDimension: true,
    aggregator: CubeField.uniqueAggregator
};

const brokerageField: CubeFieldSpec = {
    name: 'brokerage',
    type: FieldType.STRING,
    isDimension: true,
    aggregator: CubeField.uniqueAggregator
};

const quantityField: CubeFieldSpec = {
    name: 'quantity',
    type: FieldType.NUMBER,
    aggregator: CubeField.sumAggregator
};

export const marketValueField: CubeFieldSpec = {
    name: 'marketValue',
    type: FieldType.NUMBER,
    aggregator: CubeField.sumAggregator
};

const priceField: CubeFieldSpec = {
    name: 'price',
    type: FieldType.NUMBER,
    aggregator: CubeField.maxAggregator
};

const prevPriceTickField: CubeFieldSpec = {
    name: 'prevPriceTick',
    type: FieldType.NUMBER,
    aggregator: CubeField.maxAggregator
};

const priceChangeField: CubeFieldSpec = {
    name: 'priceChange',
    type: FieldType.NUMBER,
    aggregator: CubeField.maxAggregator
};

export const priceChangePctField: CubeFieldSpec = {
    name: 'priceChangePct',
    type: FieldType.NUMBER,
    aggregator: CubeField.maxAggregator
};

const avgPriceField: CubeFieldSpec = {
    name: 'avgPrice',
    type: FieldType.NUMBER,
    aggregator: new WeightedAvgPriceAggregator()
};

export const totalCostField: CubeFieldSpec = {
    name: 'totalCost',
    type: FieldType.NUMBER,
    aggregator: CubeField.sumAggregator
};

export const pnlDlyField: CubeFieldSpec = {
    name: 'pnlDly',
    type: FieldType.NUMBER,
    aggregator: CubeField.sumAggregator
};

export const pnlYtdField: CubeFieldSpec = {
    name: 'pnlYtd',
    type: FieldType.NUMBER,
    aggregator: CubeField.sumAggregator
};

export const pnlOpenField: CubeFieldSpec = {
    name: 'pnlOpen',
    type: FieldType.NUMBER,
    aggregator: CubeField.sumAggregator
};

const betaField: CubeFieldSpec = {
    name: 'beta',
    type: FieldType.NUMBER,
    aggregator: CubeField.maxAggregator
};

const lastDividendField: CubeFieldSpec = {
    name: 'lastDividend',
    type: FieldType.NUMBER,
    aggregator: CubeField.sumAggregator
};

/** 1-year range, widened by the live price. The same on every position in a ticker, so a ticker group shows it too. */
const yearLowField: CubeFieldSpec = {
    name: 'yearLow',
    type: FieldType.NUMBER,
    aggregator: CubeField.minAggregator
};

const yearHighField: CubeFieldSpec = {
    name: 'yearHigh',
    type: FieldType.NUMBER,
    aggregator: CubeField.maxAggregator
};

export const sectorField: CubeFieldSpec = {
    name: 'sector',
    type: FieldType.STRING,
    isDimension: true,
    aggregator: CubeField.uniqueAggregator
};

export const allPositionFields = [
    portfolioIdField,
    securityIdField,
    tickerField,
    portfolioNameField,
    portfolioHoldingTypeField,
    portfolioManagementTypeField,
    portfolioTaxTypeField,
    brokerageField,
    quantityField,
    marketValueField,
    priceField,
    prevPriceTickField,
    priceChangeField,
    priceChangePctField,
    avgPriceField,
    totalCostField,
    pnlDlyField,
    pnlYtdField,
    pnlOpenField,
    betaField,
    lastDividendField,
    yearLowField,
    yearHighField,
    sectorField
];

const pnlColDefaults: ColumnSpec = {
    align: 'right',
    width: 100,
    absSort: true,
    // @ts-ignore - type instantiation depth issue with numberRenderer colorSpec inference
    renderer: numberRenderer({
        precision: 0,
        ledger: true,
        colorSpec: true,
        prefix: '$'
    })
};

const priceColDefaults: ColumnSpec = {
    align: 'right',
    width: 100,
    absSort: true,
    renderer: priceRenderer({
        precision: 2,
        prefix: '$'
    }),
    rendererIsComplex: true,
    cellClassRules: {
        'flash-green': ({data: record}) => record.data.price > record.data.prevPriceTick,
        'flash-red': ({data: record}) => record.data.price < record.data.prevPriceTick
    }
};

const priceDiffColDefaults: ColumnSpec = {
    align: 'right',
    width: 100,
    absSort: true,
    renderer: priceRenderer({
        precision: 2,
        colorSpec: true,
        prefix: '$'
    }),
    rendererIsComplex: true,
    cellClassRules: {
        'flash-green': ({data: record}) => record.data.price > record.data.prevPriceTick,
        'flash-red': ({data: record}) => record.data.price < record.data.prevPriceTick
    }
};

const pctColDefaults: ColumnSpec = {
    align: 'right',
    width: 100,
    absSort: true,
    renderer: percentRenderer({
        precision: 2,
        colorSpec: true
    }),
    rendererIsComplex: true,
    cellClassRules: {
        'flash-green': ({data: record}) => record.data.price > record.data.prevPriceTick,
        'flash-red': ({data: record}) => record.data.price < record.data.prevPriceTick
    }
};

export const portfolioIdCol: ColumnSpec = {
    field: portfolioIdField,
    hidden: true
};

export const portfolioNameCol: ColumnSpec = {
    field: portfolioNameField
};

export const brokerageCol: ColumnSpec = {
    field: brokerageField
};

/** Short badge labels for the portfolio types. An unknown value shows as is. */
const PORTFOLIO_TYPE_LABELS: Record<string, string> = {
    Managed: 'Managed',
    Direct: 'Direct',
    Taxable: 'Taxable',
    TaxDeferred: 'Deferred',
    TaxFree: 'Tax-free'
};

/** A portfolio type as a colored badge, e.g. `rzx-type-badge--TaxDeferred`. Blank on a group mixing several types. */
function portfolioTypeBadge(v: string) {
    return v ? span({className: `rzx-type-badge rzx-type-badge--${v}`, item: PORTFOLIO_TYPE_LABELS[v] ?? v}) : null;
}

export const portfolioManagementTypeCol: ColumnSpec = {
    field: portfolioManagementTypeField,
    headerName: 'Mgmt',
    align: 'center',
    width: 80,
    renderer: portfolioTypeBadge
};

export const portfolioTaxTypeCol: ColumnSpec = {
    field: portfolioTaxTypeField,
    headerName: 'Tax',
    align: 'center',
    width: 80,
    renderer: portfolioTypeBadge
};

export const tickerCol: ColumnSpec = {
    field: tickerField,
    minWidth: 75,
    renderer: v => strong(v)
};

/** True for a row that stands for one ticker - a position, or a group by ticker - rather than e.g. a sector total. */
export function isTickerRow(record: StoreRecord): boolean {
    if (!record || record.isSummary) return false;
    const dim = record.raw?.cubeDimension;
    return dim == null || dim === 'ticker';
}

/** Ticker cell background by today's price change - flat covers zero and missing changes. See PositionColumns.scss. */
export const tickerChangeClassRules: Record<string, ColumnCellClassRuleFn> = {
    'rzx-ticker-cell--up': ({data: record}) => isTickerRow(record) && (record.data.priceChangePct as number) > 0,
    'rzx-ticker-cell--down': ({data: record}) => isTickerRow(record) && (record.data.priceChangePct as number) < 0,
    'rzx-ticker-cell--flat': ({data: record}) =>
        isTickerRow(record) && !((record.data.priceChangePct as number) > 0 || (record.data.priceChangePct as number) < 0)
};

export const priceCol: ColumnSpec = {
    field: priceField,
    ...priceColDefaults
};

export const prevPriceTickCol: ColumnSpec = {
    field: 'prevPriceTick',
    hidden: true,
    excludeFromChooser: true
};

export const priceChangeCol: ColumnSpec = {
    field: priceChangeField,
    ...priceDiffColDefaults,
    headerName: 'Price Chg'
};

/** Solid green/red change block - styled only under the terminal display style. See PositionColumns.scss and UiStyle.ts. */
export const changeBlockClassRules: Record<string, ColumnCellClassRuleFn> = {
    'rzx-chg-block--up': ({data: record}) => isTickerRow(record) && (record.data.priceChangePct as number) > 0,
    'rzx-chg-block--down': ({data: record}) => isTickerRow(record) && (record.data.priceChangePct as number) < 0
};

/** Direction glyph (colored arrow) at the cell's left edge - styled only under the terminal display style. */
export const changeGlyphClassRules: Record<string, ColumnCellClassRuleFn> = {
    'rzx-chg-glyph--up': ({data: record}) => isTickerRow(record) && (record.data.priceChangePct as number) > 0,
    'rzx-chg-glyph--down': ({data: record}) => isTickerRow(record) && (record.data.priceChangePct as number) < 0
};

export const priceChangePctCol: ColumnSpec = {
    field: priceChangePctField,
    ...pctColDefaults,
    headerName: 'Price Chg %',
    // Glyph only, no fill - a solid background would hide the tick flash.
    cellClassRules: {...pctColDefaults.cellClassRules, ...changeGlyphClassRules}
};

export const marketValueCol: ColumnSpec = {
    field: marketValueField,
    align: 'right',
    headerName: 'MV',
    width: 100,
    absSort: true,
    renderer: numberRenderer({
        precision: 0,
        ledger: false,
        colorSpec: false,
        prefix: '$'
    })
};

export const quantityCol: ColumnSpec = {
    field: quantityField,
    align: 'right',
    width: 100,
    absSort: true,
    renderer: numberRenderer({
        precision: 0,
        ledger: false,
        colorSpec: false
    })
};

export const avgPriceCol: ColumnSpec = {
    field: avgPriceField,
    ...priceColDefaults,
    cellClassRules: null,
    headerName: 'Cost/Share'
};

export const totalCostCol: ColumnSpec = {
    field: totalCostField,
    headerName: 'Total Cost',
    width: 120,
    align: 'right',
    absSort: true,
    renderer: numberRenderer({
        precision: 0,
        ledger: false,
        colorSpec: false,
        prefix: '$'
    })
};

export const pnlDlyCol: ColumnSpec = {
    field: pnlDlyField,
    ...pnlColDefaults,
    headerName: 'P&L'
};

export const pnlYtdCol: ColumnSpec = {
    field: pnlYtdField,
    ...pnlColDefaults,
    headerName: 'YTD P&L'
};

export const pnlOpenCol: ColumnSpec = {
    field: pnlOpenField,
    ...pnlColDefaults,
    headerName: 'LTD P&L'
};

export const betaCol: ColumnSpec = {
    field: betaField,
    renderer: numberRenderer({
        precision: 4
    })
};

export const lastDividendCol: ColumnSpec = {
    field: lastDividendField,
    renderer: v =>
        v
            ? fmtNumber(v, {
                  precision: 2,
                  prefix: '$'
              })
            : null
};

export const sectorCol: ColumnSpec = {
    field: sectorField
};

/** A ticker row for a security rather than cash - the rows that get price charts. */
function isSecurityRow(record: StoreRecord): boolean {
    return isTickerRow(record) && !startsWith(record.data.ticker as string, '$');
}

/** Shared by the chart columns, which read the live price but draw rather than print it. */
const chartColDefaults: ColumnSpec = {
    field: 'price',
    sortable: false,
    filterable: false,
    autosizable: false,
    excludeFromExport: true,
    rendererIsComplex: true
};

/** A row grouping one portfolio - the rows that get the portfolio's balance chart. */
export function isPortfolioRow(record: StoreRecord): boolean {
    return !!record && !record.isSummary && record.raw?.cubeDimension === 'portfolioName' && record.data.portfolioId != null;
}

/**
 * Today's path as a sparkline, sized to the row height. See MiniCharts.scss. A ticker row draws the ticker's prices,
 * a portfolio row the portfolio's balance. The chart observes its series in IntradayService, so it redraws on each new
 * bar without the cell refreshing. Portfolio series are loaded by the grid's model.
 */
export const intradayCol: ColumnSpec = {
    ...chartColDefaults,
    cellClass: MINI_CHART_CELL,
    headerName: 'Today',
    width: 110,
    rendererIsComplex: false,
    renderer: (v, {record}) =>
        isSecurityRow(record)
            ? intradaySparkline({ticker: record.data.ticker})
            : isPortfolioRow(record)
              ? intradaySparkline({portfolioId: record.data.portfolioId})
              : null
};

/** Gross market value of the top-level rows, cached per store update - the whole that Weight is a share of. */
const grossMvCache = new WeakMap<Store, {lastUpdated: number; value: number}>();

function grossMv(store: Store): number {
    const cached = grossMvCache.get(store);
    if (cached?.lastUpdated === store.lastUpdated) return cached.value;

    const value = sumBy(
        store.records.filter(r => !r.parentId && !r.isSummary),
        r => Math.abs((r.data.marketValue as number) ?? 0)
    );
    grossMvCache.set(store, {lastUpdated: store.lastUpdated, value});
    return value;
}

/** Share of the book's gross market value as a bar - shorts count by size, and fill red. */
export const weightCol: ColumnSpec = {
    ...chartColDefaults,
    field: 'marketValue',
    headerName: 'Weight',
    cellClass: MINI_CHART_CELL,
    width: 120,
    renderer: (v, {record}) => (record && !record.isSummary ? shareBar(v, grossMv(record.store as Store)) : null)
};

/** Today's P&L as a share of the market value at the prior close. */
export const pnlDlyPctCol: ColumnSpec = {
    ...chartColDefaults,
    field: 'pnlDly',
    headerName: 'P&L %',
    align: 'right',
    width: 80,
    renderer: (v, {record}) => {
        const prev = ((record?.data.marketValue as number) ?? 0) - ((v as number) ?? 0);
        return prev ? fmtPercent(v / Math.abs(prev), {precision: 2, withPlusSign: true, colorSpec: true}) : null;
    }
};

/** 52-week low and high on one line, with the live price marked between them. */
export const yearRangeCol: ColumnSpec = {
    ...chartColDefaults,
    headerName: '1Y Range',
    cellClass: MINI_CHART_CELL,
    width: 160,
    renderer: (v, {record}) => (isSecurityRow(record) ? rangeBar(record.data.yearLow, record.data.yearHigh, v) : null)
};

export const allPositionColumns: ColumnSpec[] = [
    //tickerCol,
    portfolioIdCol,
    priceCol,
    prevPriceTickCol,
    priceChangeCol,
    priceChangePctCol,
    marketValueCol,
    quantityCol,
    avgPriceCol,
    totalCostCol,
    pnlDlyCol,
    pnlYtdCol,
    pnlOpenCol,
    betaCol,
    lastDividendCol,
    sectorCol
];
